import {
  INVASION,
  type WorldEventEnded,
  WorldEventEndedSchema,
  type WorldEventState,
  WorldEventStateSchema,
} from '@shefira/shared';

import { cue } from '../audio/cues.js';
import { PortraitCache } from './world-event-standings.js';
import { WorldEventResult } from './world-event-result.js';
import { WorldEventTracker } from './world-event-tracker.js';

/** The little of a Colyseus room this needs. */
interface EventRoom {
  onMessage: (type: string, callback: (payload: unknown) => void) => unknown;
}

/**
 * The client's side of the world events (ADR 0050): shows what the server
 * says, once a second and at the end. It decides nothing; the maps, the
 * music and the townsfolk ask it where the event is and whether a village
 * is under attack, only to draw and to explain.
 */
export class WorldEventClient {
  private state: WorldEventState | null = null;
  private readonly portraits: PortraitCache;
  private readonly tracker: WorldEventTracker;
  private readonly result: WorldEventResult;
  private readonly listeners = new Set<() => void>();

  constructor(room: EventRoom) {
    this.portraits = new PortraitCache(() => {
      this.tracker.refresh();
    });
    this.tracker = new WorldEventTracker(this.portraits);
    this.result = new WorldEventResult(this.portraits);
    room.onMessage('world_event', (payload: unknown) => {
      const parsed = WorldEventStateSchema.safeParse(payload);
      if (parsed.success) this.apply(parsed.data);
    });
    room.onMessage('world_event_ended', (payload: unknown) => {
      const parsed = WorldEventEndedSchema.safeParse(payload);
      if (parsed.success) this.end(parsed.data);
    });
  }

  /** The event in progress, as last told. */
  get current(): WorldEventState | null {
    return this.state;
  }

  /** Called when an event appears, changes phase or ends: the maps redraw. */
  onChange(listener: () => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  /** Whether this tile is in a village under attack: its townsfolk do not trade. */
  besieges(tile: { x: number; y: number }): boolean {
    const state = this.state;
    return (
      state?.phase === 'running' && Math.hypot(tile.x - state.x, tile.y - state.y) <= state.radius
    );
  }

  /** The battle's music, for a player in the village under attack or at its gates. */
  musicAt(tile: { x: number; y: number }): string | undefined {
    const state = this.state;
    if (state?.phase !== 'running') return undefined;
    const near = Math.hypot(tile.x - state.x, tile.y - state.y) <= state.radius + INVASION.reach;
    return near ? 'music.event' : undefined;
  }

  destroy(): void {
    this.tracker.destroy();
    this.result.destroy();
    this.listeners.clear();
  }

  private apply(next: WorldEventState): void {
    const previous = this.state;
    this.state = next;
    this.tracker.show(next);
    const changed = previous?.id !== next.id || previous.phase !== next.phase;
    // The horn at the warning and when it starts: heard wherever one is in the world.
    if (changed) cue('sfx.event-horn');
    if (changed) this.changed();
  }

  private end(result: WorldEventEnded): void {
    this.state = null;
    this.tracker.show(null);
    // The ranking opens for whoever took part; the others read the announcement.
    if (result.self !== null && result.self.rank > 0) this.result.show(result);
    this.changed();
  }

  private changed(): void {
    for (const listener of this.listeners) listener();
  }
}
